import { useCallback, useRef } from "react";
import type { LeadInput, LeadUpdate, StepId } from "@autopic/shared";
import { isContactSatisfied } from "@autopic/shared";
import { api } from "../api/client";
import { track } from "../analytics/analytics";
import { useSession } from "./sessionStore";

/**
 * Autosaves a Lead the moment a usable contact detail is captured, then patches
 * it as the journey advances. Fire-and-forget: a failed save never blocks the UI.
 * Returns `saveLead(step)` which the flow calls on step transitions.
 */
export function useLeadAutosave() {
  // Guard against overlapping creates before leadId lands in the store.
  const creating = useRef(false);

  const saveLead = useCallback(async (lastStep: StepId) => {
    const s = useSession.getState();
    const { garageId, config } = s;
    if (!garageId || !config) return;

    const contact = s.contact;
    const contactReady = isContactSatisfied(config.flow.contactStrategy, contact);

    const base = {
      contact,
      vehicle: s.vehicle ?? undefined,
      postcode: s.postcode ?? undefined,
      branchId: s.branchId ?? undefined,
      serviceIds: s.selectedServiceIds.length ? s.selectedServiceIds : undefined,
      lastStep,
    };

    // Patch an existing lead.
    if (s.leadId) {
      const patch: LeadUpdate = base;
      try {
        await api.updateLead(s.leadId, patch);
      } catch {
        /* non-blocking */
      }
      return;
    }

    // Otherwise, only create once we actually have contact (a real "lead").
    if (!contactReady || creating.current) return;
    creating.current = true;
    const input: LeadInput = {
      garageId,
      sessionId: s.sessionId,
      ...base,
    };
    try {
      const lead = await api.createLead(input);
      useSession.getState().setLeadId(lead.id);
      track("lead_captured", {
        lastStep,
        hasEmail: !!contact.email,
        hasPhone: !!contact.phone,
      });
    } catch {
      // allow a retry on the next step
    } finally {
      creating.current = false;
    }
  }, []);

  return saveLead;
}
